"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import {
  ArrowRightIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentCheckIcon,
  PhoneIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { trackPhoneClick } from "../../lib/tracking";

// Variantes alignées sur src/docs/vide-maison-bruxelles-charte-couleurs-claude-code-v1.1.md
// primary = bleu (marque) · cta = vert (conversion uniquement) · secondary = blanc/bordure bleue
function MenuButton({
  children,
  href,
  onClick,
  icon: Icon,
  variant = "primary", // primary | cta | secondary
  className = "",
  external = false,
  ...props
}) {
  const baseClasses =
    "group inline-flex items-center justify-center gap-2 rounded-xl font-bold transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 whitespace-nowrap";

  const variants = {
    primary:
      "bg-brand-900 text-white shadow-premiumSm hover:bg-brand-950",

    cta:
      "bg-cta-700 text-white shadow-cta hover:bg-cta-800 active:bg-cta-900",

    secondary:
      "border border-brand-700 bg-white text-brand-900 hover:border-cta-300 hover:bg-cta-50 hover:text-cta-900",
  };

  const iconColor =
    variant === "secondary" ? "text-brand-700 group-hover:text-cta-700" : "text-white/85";

  const content = (
    <>
      {Icon && (
        <Icon
          className={`h-5 w-5 shrink-0 transition-transform duration-300 group-hover:scale-110 ${iconColor}`}
        />
      )}

      <span>{children}</span>

      {!Icon && (
        <ArrowRightIcon className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </>
  );

  const classes = `${baseClasses} ${variants[variant]} ${className}`;

  if (href) {
    if (external || href.startsWith("tel:") || href.startsWith("mailto:")) {
      return (
        <a href={href} onClick={onClick} className={classes} {...props}>
          {content}
        </a>
      );
    }

    return (
      <Link href={href} onClick={onClick} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={classes} {...props}>
      {content}
    </button>
  );
}

function Logo() {
  return (
    <span className="flex items-center gap-2.5">
      <Image
        src="/logo/logo-vide-maison-bruxelles-icon.png"
        alt=""
        width={80}
        height={80}
        className="h-16 w-16 shrink-0 rounded-full md:h-20 md:w-20"
        priority
      />
      <span className="flex flex-col leading-none">
        <span className="font-serif text-lg font-bold tracking-tight text-ink md:text-xl">Vide Maison</span>
        <span className="text-xs font-bold uppercase tracking-[0.14em] text-accent md:text-sm">Bruxelles</span>
      </span>
    </span>
  );
}

export default function MenuBar() {
  const pathname = usePathname();
  const t = useTranslations("Nav");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);

  const PHONE_NUMBER = "+32 (0)493 96 65 07";
  const PHONE_LINK = "+32493966507";

  // "Accueil" est retiré : le logo renvoie déjà vers l'accueil. Le rachat
  // d'objets est intégré au dropdown Vide maison (visuellement séparé),
  // plutôt que d'être son propre item de premier niveau — même organisation
  // que macha-vide-maison (src/lib/menu.js) pour éviter une barre de menu
  // surchargée.
  const MENU_ITEMS = [
    {
      key: "videMaison",
      children: [
        { key: "videMaisonAllCommunes", link: "/vide-maison" },
        { key: "communeIxelles", link: "/vide-maison/ixelles" },
        { key: "communeUccle", link: "/vide-maison/uccle" },
        { key: "communeSchaerbeek", link: "/vide-maison/schaerbeek" },
        { key: "communeAnderlecht", link: "/vide-maison/anderlecht" },
        { key: "rachatObjets", link: "/rachat-objets", separator: true },
      ],
    },
    {
      key: "debarras",
      children: [
        { key: "debarrasAppartement", link: "/debarras-appartement" },
        { key: "debarrasMaison", link: "/debarras-maison" },
        { key: "debarrasCaveGrenierGarage", link: "/debarras-cave-grenier-garage" },
      ],
    },
    { key: "tarifs", link: "/tarifs" },
    { key: "realisations", link: "/realisations" },
    { key: "avisClients", link: "/avis" },
    { key: "contact", link: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const closeDropdowns = () => setOpenDropdown(null);
    window.addEventListener("scroll", closeDropdowns, { passive: true });
    return () => window.removeEventListener("scroll", closeDropdowns);
  }, []);

  useEffect(() => {
    if (!open) setOpenMobileGroup(null);
  }, [open]);

  const isActive = (link) =>
    link === "/" ? pathname === "/" : pathname === link || pathname?.startsWith(`${link}/`);

  if (pathname?.startsWith("/admin")) return null;

  return (
    <header className="sticky top-3 z-50 w-full">
      <div className="px-4 sm:px-6 md:px-10 lg:px-24 xl:px-36">
        <div
          className={[
            "relative overflow-visible rounded-[24px] border bg-white/95 backdrop-blur transition-shadow duration-300",
            scrolled
              ? "border-brand-100 shadow-[0_16px_40px_rgba(23,59,92,0.18)]"
              : "border-sand-line shadow-[0_14px_38px_rgba(23,59,92,0.16)]",
          ].join(" ")}
        >
        <nav className="relative px-2 sm:px-4">
          <div className="flex h-20 items-center justify-between gap-3 md:h-24">
            <Link href="/" className="relative z-10 flex shrink-0 items-center">
              <Logo />
            </Link>

            <span
              aria-hidden="true"
              className="mx-2 hidden h-12 w-px shrink-0 bg-gradient-to-b from-transparent via-sand-line to-transparent lg:block xl:mx-4"
            />

            <div className="hidden min-w-0 flex-1 items-center justify-center gap-3 lg:flex xl:gap-4 2xl:gap-5">
              {MENU_ITEMS.map((item, index) => {
                if (!item.children) {
                  const active = isActive(item.link);
                  return (
                    <Link
                      key={index}
                      href={item.link}
                      className={[
                        "group relative whitespace-nowrap py-2 text-[13px] tracking-wide transition-colors duration-200 xl:text-[14px] 2xl:text-[15px]",
                        active ? "font-semibold text-cta-800" : "font-bold text-ink/85 hover:text-cta-700",
                      ].join(" ")}
                    >
                      {t(item.key)}
                      <span
                        className={[
                          "absolute -bottom-1 left-0 h-[2px] rounded-full bg-cta-700 transition-all duration-300",
                          active ? "w-full" : "w-0 group-hover:w-full",
                        ].join(" ")}
                      />
                    </Link>
                  );
                }

                const isOpen = openDropdown === item.key;

                return (
                  <div
                    key={index}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.key)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      type="button"
                      className="group relative inline-flex items-center gap-1 whitespace-nowrap py-2 text-[13px] font-bold tracking-wide text-ink/85 transition-colors duration-200 hover:text-cta-700 xl:text-[14px] 2xl:text-[15px]"
                      aria-expanded={isOpen}
                    >
                      <span>{t(item.key)}</span>
                      <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                      <span
                        className={`absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-cta-700 transition-all duration-300 ${
                          isOpen ? "w-full" : "group-hover:w-full"
                        }`}
                      />
                    </button>

                    <div
                      className={`absolute left-0 top-full pt-3 transition-all duration-200 ${
                        isOpen
                          ? "pointer-events-auto translate-y-0 opacity-100"
                          : "pointer-events-none translate-y-1 opacity-0"
                      }`}
                    >
                      <div className="min-w-[260px] rounded-xl border border-sand-line bg-white p-2 shadow-premium">
                        {item.children.map((child, childIndex) => (
                          <Link
                            key={childIndex}
                            href={child.link}
                            className={`block whitespace-nowrap rounded-lg px-4 py-3 text-[15px] font-semibold text-ink/85 transition hover:bg-cta-50 hover:text-cta-900 ${
                              child.separator ? "mt-1 border-t border-sand-line pt-3" : ""
                            }`}
                          >
                            {t(child.key)}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden shrink-0 items-center gap-2 lg:flex">
              <LanguageSwitcher variant="light" />

              <MenuButton
                href={`tel:${PHONE_LINK}`}
                onClick={(e) => trackPhoneClick(e, { phoneNumber: PHONE_LINK, location: "navbar_desktop" })}
                aria-label={`${t("call")} ${PHONE_NUMBER}`}
                icon={PhoneIcon}
                variant="cta"
                className="h-12 px-4 text-[13px] xl:px-5 xl:text-[14px]"
              >
                {t("callNow")}
              </MenuButton>

              <MenuButton
                href="/contact"
                icon={ClipboardDocumentCheckIcon}
                variant="secondary"
                className="h-12 px-4 text-[13px] xl:px-5 xl:text-[14px]"
              >
                {t("freeQuote")}
              </MenuButton>
            </div>

            <button
              aria-label={open ? t("closeMenu") : t("openMenu")}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex shrink-0 items-center justify-center rounded-xl border border-sand-line bg-sand p-2.5 text-ink transition hover:bg-sand-dark lg:hidden"
            >
              {open ? (
                <XMarkIcon className="h-7 w-7" />
              ) : (
                <Bars3Icon className="h-7 w-7" />
              )}
            </button>
          </div>

          <div
            className={`overflow-hidden border-t border-sand-line transition-all duration-300 lg:hidden ${
              open ? "max-h-[900px] pb-4" : "max-h-0 border-t-0"
            }`}
          >
            <div className="mt-3 rounded-xl border border-sand-line bg-white p-2">
              <div className="grid grid-cols-2 gap-2 p-1">
                <MenuButton
                  href={`tel:${PHONE_LINK}`}
                  onClick={(e) => trackPhoneClick(e, { phoneNumber: PHONE_LINK, location: "navbar_mobile" })}
                  aria-label={`${t("call")} ${PHONE_NUMBER}`}
                  icon={PhoneIcon}
                  variant="cta"
                  className="h-12 px-3 text-[14px]"
                >
                  {t("call")}
                </MenuButton>

                <MenuButton
                  href="/contact"
                  onClick={() => setOpen(false)}
                  icon={ClipboardDocumentCheckIcon}
                  variant="secondary"
                  className="h-12 px-3 text-[14px]"
                >
                  {t("freeQuote")}
                </MenuButton>
              </div>

              <div className="mt-2 flex flex-col gap-1">
                {MENU_ITEMS.map((item, index) => {
                  if (!item.children) {
                    const active = isActive(item.link);
                    return (
                      <Link
                        key={index}
                        href={item.link}
                        onClick={() => setOpen(false)}
                        className={[
                          "rounded-lg px-4 py-3 text-[16px] transition hover:bg-sand",
                          active ? "font-semibold text-cta-800" : "font-semibold text-ink/85",
                        ].join(" ")}
                      >
                        <span className="inline-flex items-center gap-3">
                          <span className="h-5 w-[3px] rounded-full bg-accent" />
                          {t(item.key)}
                        </span>
                      </Link>
                    );
                  }

                  const isMobileGroupOpen = openMobileGroup === item.key;

                  return (
                    <div key={index} className="overflow-hidden rounded-lg">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenMobileGroup((prev) =>
                            prev === item.key ? null : item.key
                          )
                        }
                        className="flex w-full items-center justify-between px-4 py-3 text-left text-[16px] font-semibold text-ink/85 transition hover:bg-sand"
                        aria-expanded={isMobileGroupOpen}
                      >
                        <span className="inline-flex items-center gap-3">
                          <span className="h-5 w-[3px] rounded-full bg-accent" />
                          {t(item.key)}
                        </span>
                        <ChevronDownIcon
                          className={`h-5 w-5 transition-transform duration-200 ${
                            isMobileGroupOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          isMobileGroupOpen ? "max-h-80" : "max-h-0"
                        }`}
                      >
                        <div className="flex flex-col gap-1 px-2 pb-2 pl-6 pt-1">
                          {item.children.map((child, childIndex) => (
                            <Link
                              key={childIndex}
                              href={child.link}
                              onClick={() => {
                                setOpen(false);
                                setOpenMobileGroup(null);
                              }}
                              className={`rounded-lg px-4 py-3 text-[15px] font-medium text-ink/75 transition hover:bg-sand ${
                                child.separator ? "mt-1 border-t border-sand-line pt-3" : ""
                              }`}
                            >
                              {t(child.key)}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-2 border-t border-sand-line pt-2">
                  <LanguageSwitcher variant="light" />
                </div>
              </div>
            </div>
          </div>
        </nav>
        </div>
      </div>
    </header>
  );
}
